/**
 * legendModel — the single legend-derivation source of truth.
 *
 * Turns a Layer into a normalized, render-agnostic legend, consumed by BOTH the
 * legend widget and the Print/PDF canvas. Unlike the old widget (which only
 * legended WMS layers), this derives a legend for every active layer from its
 * symbology — including geojson/live layers — and excludes backgrounds.
 */

import { Layer } from '../../../types/layer';
import { getLegendUrl, getProgrammaticLegendItems } from './LegendService';
import { getCustomLegend, getCustomLegendTitle } from './customLegends';
import { getDefaultIcon } from '../../layers2/utils/iconUtils';
import { getSymbologyFromStyle, getSymbologyLegendEntries } from '../../layers2/layerSymbology';

const DEFAULT_ICON_COLOR = '#3b82f6';

/**
 * Strip the FontAwesome style prefix from a class string to the bare icon name
 * faIconToSvg expects: "fa-solid fa-cloud-bolt" → "cloud-bolt", "plane" → "plane".
 * Returns null when no parsable name is present.
 */
function parseFaIconClass(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  const match = raw.match(/fa-(?:solid|regular|brands|light|thin|duotone)\s+fa-([a-z0-9-]+)/i);
  if (match) return match[1];
  const bare = raw.replace(/^fa-/, '').trim();
  return /^[a-z0-9-]+$/i.test(bare) ? bare : null;
}

export type LegendSymbol =
  | { kind: 'image'; url: string }
  | { kind: 'images'; urls: string[]; size: number }
  | { kind: 'swatch'; color: string; shape: 'circle' | 'line' | 'fill' }
  | { kind: 'icon'; iconName: string; color: string; size: number };

export interface LegendEntry {
  label: string;
  symbol: LegendSymbol;
}

export interface LayerLegend {
  layerId: string;
  layerName: string;
  entries: LegendEntry[];
}

/**
 * Derive the legend for a single layer, or null when none applies (background
 * layer, or a layer with no symbology we can represent).
 */
export function getLayerLegend(layer: Layer): LayerLegend | null {
  if (layer.category === 'backgrounds') return null;

  const entries = deriveEntries(layer);
  if (entries.length === 0) return null;

  return { layerId: layer.id, layerName: getCustomLegendTitle(layer) ?? layer.name, entries };
}

/** Derive legends for all active layers, excluding backgrounds and the undecorated. */
export function getActiveLegends(layers: Layer[]): LayerLegend[] {
  return layers.map(getLayerLegend).filter((l): l is LayerLegend => l !== null);
}

function deriveEntries(layer: Layer): LegendEntry[] {
  // 0. Provider-declared custom legend (complex symbology — single source of
  //    truth, sourced from the provider's own symbology functions). Wins over
  //    the auto path so e.g. Live Weather shows its real RH ramp, not its icon.
  const custom = getCustomLegend(layer);
  if (custom) return custom;

  // 1. Programmatic colour swatches (e.g. road classes, danger classes).
  const programmatic = getProgrammaticLegendItems(layer);
  if (programmatic) {
    return programmatic.map((item) => ({
      label: item.label,
      symbol: { kind: 'swatch', color: item.color, shape: 'fill' },
    }));
  }

  // 2. A legend image (direct legend_url, EM2 config.legendUrl, or WMS-built).
  const url = getLegendUrl(layer);
  if (url) {
    return [{ label: '', symbol: { kind: 'image', url } }];
  }

  // 3. Marker-provider live layers carry a config.icon (FA class), not a
  //    config.style. Represent each with its declared icon.
  if (layer.type === 'live') {
    return deriveLiveEntries(layer);
  }

  // 4. Derive from the layer's draw style (live/geojson layers).
  return deriveStyleEntries(layer);
}

function deriveLiveEntries(layer: Layer): LegendEntry[] {
  const config = layer.config || {};
  const iconName = parseFaIconClass(config.icon);
  if (!iconName) return [];
  return [{
    label: '',
    symbol: {
      kind: 'icon',
      iconName,
      color: config.iconColor || DEFAULT_ICON_COLOR,
      size: 1,
    },
  }];
}

function deriveStyleEntries(layer: Layer): LegendEntry[] {
  const style = layer.config?.style;
  if (!style) return [];

  // Categorical symbology (fire Active/Out) — same descriptor that renders it.
  const symbology = getSymbologyFromStyle(style);
  if (symbology) {
    return getSymbologyLegendEntries(symbology);
  }

  switch (style.type) {
    case 'symbol':
    case 'fa-marker':
      return [{
        label: '',
        symbol: {
          kind: 'icon',
          iconName: style.icon || getDefaultIcon(layer.name),
          color: style.paint?.['icon-color'] || DEFAULT_ICON_COLOR,
          size: style.paint?.['icon-size'] || 1,
        },
      }];
    case 'circle':
      return [{ label: '', symbol: { kind: 'swatch', color: style.paint?.['circle-color'] || DEFAULT_ICON_COLOR, shape: 'circle' } }];
    case 'line':
      return [{ label: '', symbol: { kind: 'swatch', color: style.paint?.['line-color'] || DEFAULT_ICON_COLOR, shape: 'line' } }];
    case 'fill':
      return [{ label: '', symbol: { kind: 'swatch', color: style.paint?.['fill-color'] || DEFAULT_ICON_COLOR, shape: 'fill' } }];
    default:
      return [];
  }
}
